import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import TableContainer from "../../../components/Common/TableContainer";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  FormFeedback,
  Input,
  Form,
  UncontrolledTooltip,
} from "reactstrap";
import Select from "react-select";
import * as Yup from "yup";
import { useFormik } from "formik";
import toast from 'react-hot-toast';
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { createSponsor, deleteSponsor, getAllEvents, getAllSponsors, getAllSponsorTypes } from "api";
import { getUserRole } from "utils/storage";

const SponsorList = () => {

  //meta title
  document.title = "Sponsor List | EventoX";
  const [sponsors, setSponsors] = useState([]);
  const [sponsorTypes, setSponsorTypes] = useState([]);
  const [selectedSponsorType, setSelectedSponsorType] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSponsorId, setSelectedSponsorId] = useState(null);
  const [modal, setModal] = useState(false);
  const [isLoading, setLoading] = useState(false)
  const [selectedImage, setSelectedImage] = useState("")
  const [deleteModal, setDeleteModal] = useState(false);
  
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      eventSlug: "",
      sponsorTypeId: "",
      url: "",
      profileImage: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter Name"),
      eventSlug: Yup.string().required("Please Select Event Slug"),
      sponsorTypeId: Yup.string().required("Please Select Sponsor Type"),
      // url: Yup.string().url("Enter a valid URL").required("Please Enter URL"),
      //profileImage: Yup.mixed().required("Please Select Image"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("link", values.url);
      formData.append("eventSlug", values.eventSlug);
      formData.append("sponsorTypeId", values.sponsorTypeId);
      formData.append("thumb", values.profileImage);

      try {
        const response = await createSponsor(formData);
        if (response && response.success === true) {
          toast.success("Sponsor Created Successfully");
          const sponsorResponse = await getAllSponsors();
          getSponsor();
          toggle();
          validation.resetForm();
          setSelectedSponsorType("");
          setSelectedImage("");
        } else {
          toast.error("Failed to create sponsor");
        }
      } catch (error) {
        console.error("Create sponsor error:", error);
        toast.error("Error creating sponsor");
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    getSponsor();
  }, [selectedEvent]);


  const getSponsor=async()=>{
    try {
      const response = await getAllSponsors();
      if (selectedEvent && selectedEvent.value) {
        if (selectedEvent.value === "ALL") {
          setSponsors(response.data);
        } else {
          getSponsorByEvent(selectedEvent.value,response.data);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  const getSponsorByEvent = (slug,res) => {
    try {
      const response = res.filter((s) => s.eventSlug === slug);
      setSponsors(response);
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong!");
    }
  };


  const getSponsorTypes = async () => {
    try {
      const response = await getAllSponsorTypes();
      setSponsorTypes(response);
    } catch (error) {
      console.log(error);
    }
  };

  const sponsorTypeOptions = sponsorTypes.map(type => ({
    value: type.sponsorTypeId,
    label: type.title,
  }));
  

  const getEvents = async () => {
    try {
      const response = await getAllEvents();
      setEvents(response);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getAllSponsors();
        const role = getUserRole();
        if (role === 1 || role === '1') {
          setSponsors(response?.data);
        } else {
          setSponsors([]);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
    getEvents();
    getSponsorTypes();
  }, []);

  const eventOptions = useMemo(() => {
    return events.map(event => ({
        value: event.slugId,
        label: event.eventName,
    }))
}, [events])

  const toggle = () => {
    setModal(!modal);
  };

  const onClickDelete = (id) => {
    setSelectedSponsorId(id);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedSponsorId) return;
    try {
      const response = await deleteSponsor(selectedSponsorId);
      if (response && response.success === true) {
        const updatedSponsors = await getAllSponsors();
        setSponsors(updatedSponsors?.data);
        getSponsor();
        toast.success("Sponsor deleted successfully");
      } else {
        toast.error(response.error || "Failed to delete sponsor");
      }
    } catch (error) {
      toast.error("Error deleting sponsor");
    } finally {
      setDeleteModal(false);
      setSelectedSponsorId(null);
    }
  };

  const handleUserClicks = () => {
    toggle();
  };

  const handleImageChange = (event) => {
    event.preventDefault();
    let reader = new FileReader();
    let file = event.target.files[0];
    reader.onloadend = () => {
      setSelectedImage(reader.result);
      validation.setFieldValue('profileImage', file)
    };
    reader.readAsDataURL(file);
  };

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "thumb",
        cell: (cell) => (
          <>
            {!cell.getValue() ? (
              <div className="avatar-xs">
                <span className="avatar-title rounded-circle">{cell.row.original.name.charAt(0)} </span>
              </div>
            ) : (
              <div>
                <img className="rounded-circle avatar-xs" src={cell.getValue()} alt="" />
              </div>
            )}
          </>
        ),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Name',
        accessorKey: 'name',
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell) => {
          return (
            <>
              <h5 className='font-size-14 mb-1'>
                <Link to='#' className='text-dark'>{cell.getValue()}</Link>
              </h5>
              <p className="text-muted mb-0">{cell.row.original.designation}</p>
            </>
          )
        }
      },
      {
        header: 'Event Slug',
        accessorKey: 'eventSlug',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Link',
        accessorKey: 'link',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Sponsor Type',
        accessorFn: row => row.sponsorType?.title,
        id: 'sponsorTypeId',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Action',
        cell: (cellProps) => {
          return (
            <div className="d-flex gap-3">
              <div className="text-success btn-hover">
                <i className="mdi mdi-pencil font-size-18" id="edittooltip" />
              </div>
              <Link
                to="#"
                className="text-danger"
                onClick={() => {
                  const data = cellProps.row.original; onClickDelete(data.sponsorId);
                }}>
                <i className="mdi mdi-delete font-size-18" id="deletetooltip" />
              </Link>
            </div>
          );
        }
      },
    ],
    []
  );


  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setDeleteModal(false);
          setSelectedSponsorId(null);
        }}
      />
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs title="Dashboard" breadcrumbItem="Sponsor List" />
          <Row>
            {
              isLoading ? <Spinners setLoading={setLoading} />
                :
                <Col lg="12">
                  <Card>
                    <CardBody>
                      <Row className="mb-3">
                        <Col xl="5">
                          <div className="col-md-12">
                            <Select
                              value={selectedEvent}
                              placeholder="Select Event"
                              onChange={(data) => {
                                setSelectedEvent(data);
                                getSponsor();
                              }}
                              options={[
                                { value: "ALL", label: "All Events" },
                                ...events.map((event) => ({
                                  value: event.slugId,
                                  label: event.eventName,
                                }))
                              ]}
                              width="100%"
                              className="select2-selection"
                            />

                          </div>
                        </Col>
                      </Row>
                      <TableContainer
                        columns={columns}
                        data={sponsors || []}
                        isGlobalFilter={true}
                        isPagination={true}
                        SearchPlaceholder="Search..."
                        isCustomPageSize={true}
                        isAddButton={true}
                        handleUserClick={handleUserClicks}
                        buttonClass="btn btn-success btn-rounded waves-effect waves-light addContact-modal mb-2"
                        buttonName="New Sponsor"
                        tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                        theadClass="table-light"
                        paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                        pagination="pagination"
                      />
                    </CardBody>
                  </Card>
                </Col>
            }

            <Modal isOpen={modal} toggle={toggle}>
              <ModalHeader toggle={toggle} tag="h4">Add Sponsor</ModalHeader>
              <ModalBody>
                <Form onSubmit={e => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}>
                  <Row>
                    <Col xs={12}>
                      <div className="mb-3">
                        <Label className="form-label">Name</Label>
                        <Input
                          name="name"
                          type="text"
                          placeholder="Name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.name || ""}
                          invalid={
                            validation.touched.name &&
                              validation.errors.name
                              ? true
                              : false
                          }
                        />
                        {validation.touched.name &&
                          validation.errors.name ? (
                          <FormFeedback type="invalid">
                            {validation.errors.name}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                      <Label className="form-label">Event</Label>
                      <Select
                        name="eventSlug"
                        options={eventOptions}
                        placeholder="Select Event"
                        value={eventOptions.find(opt => opt.value === validation.values.eventSlug) || null}
                        onChange={(option) => validation.setFieldValue("eventSlug", option?.value || "")}
                        onBlur={() => validation.setFieldTouched("eventSlug", true)}
                        classNamePrefix="rs"
                        className={validation.touched.eventSlug && validation.errors.eventSlug ? "is-invalid" : ""}
                        styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
                      />

                      {validation.touched.eventSlug && validation.errors.eventSlug ? (
                        <FormFeedback type="invalid" className="d-block">
                          {validation.errors.eventSlug}
                        </FormFeedback>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <Label className="form-label">Sponsor Type</Label>
                      <Select
                        name="sponsorTypeId"
                        options={sponsorTypeOptions}
                        placeholder="Select Sponsor Type"
                        value={sponsorTypeOptions.find(opt => opt.value === validation.values.sponsorTypeId) || null}
                        onChange={(option) => validation.setFieldValue("sponsorTypeId", option?.value || "")}
                        onBlur={() => validation.setFieldTouched("sponsorTypeId", true)}
                        classNamePrefix="rs"
                        className={validation.touched.sponsorTypeId && validation.errors.sponsorTypeId ? "is-invalid" : ""}
                        styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
                      />

                      {validation.touched.sponsorTypeId && validation.errors.sponsorTypeId ? (
                        <FormFeedback type="invalid" className="d-block">
                          {validation.errors.sponsorTypeId}
                        </FormFeedback>
                      ) : null}
                    </div>


                      <div className="mb-3">
                        <Label className="form-label">Link</Label>
                        <Input
                          name="url"
                          label="URL"
                          type="text"
                          placeholder="https://www.example.com"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.url || ""}
                        />
                      </div>
                      <div className="mb-3">
                        <Label className="form-label">Thumb Image</Label>
                        <div className="text-center">
                          <div className="position-relative d-inline-block">
                            <div className="position-absolute bottom-0 end-0">
                              <Label htmlFor="project-image-input" className="mb-0" id="projectImageInput">
                                <div className="avatar-xs">
                                  <div className="avatar-title bg-light border rounded-circle text-muted cursor-pointer shadow font-size-16">
                                    <i className="bx bxs-image-alt"></i>
                                  </div>
                                </div>
                              </Label>
                              <UncontrolledTooltip placement="right" target="projectImageInput">
                                Select Image
                              </UncontrolledTooltip>
                              <input className="form-control d-none" id="project-image-input" type="file" accept="image/png, image/gif, image/jpeg" onChange={handleImageChange} />
                            </div>
                            <div className="avatar-lg">
                              <div className="avatar-title bg-light rounded-circle">
                                <img src={selectedImage || ''} id="projectlogo-img" alt="" className="avatar-md h-auto rounded-circle" />
                              </div>
                            </div>
                          </div>
                          {validation.touched.projectImage && validation.errors.projectImage ? (
                            <FormFeedback type="invalid" className="d-block">{validation.errors.projectImage}</FormFeedback>
                          ) : null}
                        </div>
                      </div>

                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="text-end">
                        <button
                          type="submit"
                          className="btn btn-success save-user"
                        >
                          {isLoading ? "Loading...." : "Save"}
                        </button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </ModalBody>
            </Modal>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default withRouter(SponsorList);
